// Storage Helper - Unified interface for localStorage fallback and IndexedDB
import indexedDBAdapter from '../services/indexedDBAdapter.js';

class StorageHelper {
  constructor() {
    this.useIndexedDB = true;
    this.initialized = false;
  }

  async init() {
    if (this.initialized) return;

    try {
      await indexedDBAdapter.init();
      this.initialized = true;
    } catch (error) {
      console.warn('IndexedDB not available, falling back to localStorage:', error);
      this.useIndexedDB = false;
      this.initialized = true;
    }
  }

  // Generic get item with fallback
  async getItem(key) {
    await this.init();

    if (this.useIndexedDB) {
      try {
        // Try autosave store first, then videos store
        let result = await indexedDBAdapter.getAutosave(key);
        if (result === null) {
          result = await indexedDBAdapter.getVideoPosition(key);
        }
        return result ? (typeof result === 'string' ? result : JSON.stringify(result)) : null;
      } catch (error) {
        console.warn('IndexedDB getItem failed, falling back to localStorage:', error);
      }
    }

    return localStorage.getItem(key);
  }

  // Generic set item with fallback
  async setItem(key, value) {
    await this.init();

    if (this.useIndexedDB) {
      try {
        // Determine store based on key pattern
        if (key.includes('autosave_') || key === 'bible_workflow_autosave') {
          await indexedDBAdapter.setAutosave(key, value);
        } else if (key.includes('video_') && key.includes('_positions')) {
          await indexedDBAdapter.setVideoPosition(key, value);
        } else {
          // Fallback to autosave store for unknown keys
          await indexedDBAdapter.setAutosave(key, value);
        }
        return;
      } catch (error) {
        console.warn('IndexedDB setItem failed, falling back to localStorage:', error);
      }
    }

    localStorage.setItem(key, value);
  }

  // Generic remove item with fallback
  async removeItem(key) {
    await this.init();

    if (this.useIndexedDB) {
      try {
        // Try both stores
        await indexedDBAdapter.removeAutosave(key);
        await indexedDBAdapter.removeVideoPosition(key);
        return;
      } catch (error) {
        console.warn('IndexedDB removeItem failed, falling back to localStorage:', error);
      }
    }

    localStorage.removeItem(key);
  }

  // Synchronous fallback for immediate localStorage operations
  // Use only when async operations aren't possible
  getItemSync(key) {
    return localStorage.getItem(key);
  }

  setItemSync(key, value) {
    localStorage.setItem(key, value);
  }

  removeItemSync(key) {
    localStorage.removeItem(key);
  }

  // Check if we're using IndexedDB
  isUsingIndexedDB() {
    return this.useIndexedDB && this.initialized;
  }

  // Get all video position keys
  async getAllVideoKeys() {
    await this.init();

    if (this.useIndexedDB) {
      try {
        return await indexedDBAdapter.getAllVideoKeys();
      } catch (error) {
        console.warn('IndexedDB getAllVideoKeys failed, falling back to localStorage:', error);
      }
    }

    // Fallback to localStorage scanning
    const keys = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.includes('video_') && key.includes('_positions')) {
        keys.push(key);
      }
    }
    return keys;
  }

  // Get storage info
  async getStorageInfo() {
    await this.init();

    const info = {
      backend: this.useIndexedDB ? 'IndexedDB' : 'localStorage',
      capacity: 'unlimited'
    };

    if (this.useIndexedDB) {
      try {
        const estimate = await indexedDBAdapter.getStorageEstimate();
        info.quota = estimate.quota;
        info.usage = estimate.usage;
        info.available = estimate.quota - estimate.usage;
      } catch (error) {
        console.warn('Could not get storage estimate:', error);
      }
    } else {
      info.capacity = '~5-10MB (localStorage limit)';
    }

    return info;
  }
}

export default new StorageHelper();